import styled, { css } from 'styled-components';
import {
  PROMO_BANNER_DENSITY_COMPACT,
  PROMO_BANNER_VARIANT_PROMINENT,
  type PromoBannerDensity,
  type PromoBannerTone,
  type PromoBannerVariant,
} from './PromoBanner.constants';

const TONE_PALETTE: Record<
  PromoBannerTone,
  (theme: { colors?: Record<string, string | undefined> }) => {
    fg: string;
    border: string;
    bgSubtle: string;
    bgProminent: string;
    fgProminent: string;
  }
> = {
  info: ({ colors = {} }) => {
    const fg = colors.primary ?? '#C1C1FF';
    return {
      fg,
      border: `color-mix(in oklch, ${fg} 40%, transparent)`,
      bgSubtle: `color-mix(in oklch, ${fg} 12%, transparent)`,
      bgProminent: fg,
      fgProminent: colors.onPrimary ?? '#13131B',
    };
  },
  success: ({ colors = {} }) => {
    const fg = colors.success ?? '#8CD88C';
    return {
      fg,
      border: `color-mix(in oklch, ${fg} 40%, transparent)`,
      bgSubtle: `color-mix(in oklch, ${fg} 14%, transparent)`,
      bgProminent: fg,
      fgProminent: colors.onSuccess ?? '#13131B',
    };
  },
  caution: ({ colors = {} }) => {
    const fg = colors.caution ?? '#FFDF9E';
    return {
      fg,
      border: `color-mix(in oklch, ${fg} 40%, transparent)`,
      bgSubtle: `color-mix(in oklch, ${fg} 16%, transparent)`,
      bgProminent: fg,
      fgProminent: colors.onCaution ?? '#13131B',
    };
  },
  error: ({ colors = {} }) => {
    const fg = colors.error ?? '#FFB4AB';
    return {
      fg,
      border: `color-mix(in oklch, ${fg} 40%, transparent)`,
      bgSubtle: `color-mix(in oklch, ${fg} 14%, transparent)`,
      bgProminent: fg,
      fgProminent: colors.onError ?? '#13131B',
    };
  },
  promo: ({ colors = {} }) => {
    const fg = colors.tertiary ?? colors.primary ?? '#C1C1FF';
    return {
      fg,
      border: `color-mix(in oklch, ${fg} 50%, transparent)`,
      bgSubtle: `color-mix(in oklch, ${fg} 16%, transparent)`,
      bgProminent: fg,
      fgProminent:
        colors.onTertiary ?? colors.onPrimary ?? '#13131B',
    };
  },
};

interface DensityProps {
  $density: PromoBannerDensity;
}

const isCompact = ({ $density }: DensityProps) => $density === PROMO_BANNER_DENSITY_COMPACT;

interface RootProps extends DensityProps {
  $tone: PromoBannerTone;
  $variant: PromoBannerVariant;
}

export const Root = styled.section<RootProps>`
  display: flex;
  align-items: ${(p) => (isCompact(p) ? 'center' : 'flex-start')};
  gap: ${(p) => (isCompact(p) ? '8px' : '12px')};
  padding: ${(p) => (isCompact(p) ? '8px 12px' : '14px 16px')};
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.large ?? borderRadius.small ?? '12px'};

  ${({ $tone, $variant, theme }) => {
    const palette = TONE_PALETTE[$tone]?.(theme) ?? TONE_PALETTE.info(theme);
    if ($variant === PROMO_BANNER_VARIANT_PROMINENT) {
      return css`
        background: ${palette.bgProminent};
        color: ${palette.fgProminent};
        border: 1px solid transparent;
      `;
    }
    return css`
      background: ${palette.bgSubtle};
      color: ${palette.fg};
      border: 1px solid ${palette.border};
    `;
  }}
`;

export const IconSlot = styled.span<DensityProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${(p) => (isCompact(p) ? '20px' : '28px')};
  height: ${(p) => (isCompact(p) ? '20px' : '28px')};
  margin-top: ${(p) => (isCompact(p) ? '0' : '-2px')};
  & > svg,
  & > .material-symbols-rounded {
    width: ${(p) => (isCompact(p) ? '18px' : '22px')};
    height: ${(p) => (isCompact(p) ? '18px' : '22px')};
    font-size: ${(p) => (isCompact(p) ? '18px' : '22px')};
  }
`;

export const Body = styled.div<DensityProps>`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: ${(p) => (isCompact(p) ? 'row' : 'column')};
  flex-wrap: ${(p) => (isCompact(p) ? 'wrap' : 'nowrap')};
  align-items: ${(p) => (isCompact(p) ? 'center' : 'stretch')};
  gap: ${(p) => (isCompact(p) ? '4px 8px' : '6px')};
`;

export const TitleRow = styled.div<DensityProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: ${(p) => (isCompact(p) ? '1 1 240px' : '0 1 auto')};
`;

export const Title = styled.h3<DensityProps>`
  margin: 0;
  font-size: ${(p) => (isCompact(p) ? '13px' : '14px')};
  font-weight: ${(p) => (isCompact(p) ? 500 : 600)};
  line-height: ${(p) => (isCompact(p) ? 1.5 : 'normal')};
  white-space: ${(p) => (isCompact(p) ? 'normal' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;
  color: inherit;
`;

export const Description = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: inherit;
  opacity: 0.9;
`;

export const Actions = styled.div<DensityProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: ${(p) => (isCompact(p) ? '0' : '4px')};
  margin-left: ${(p) => (isCompact(p) ? 'auto' : '0')};
`;

export const DismissButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin: -4px -6px -4px 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  ${({ theme: { motion } }) => css`
    transition: background ${motion?.duration?.fast ?? '120ms'}
      ${motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};
  `}

  &:hover {
    background: color-mix(in oklch, currentColor 12%, transparent);
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  & > svg {
    width: 18px;
    height: 18px;
  }
`;
